import React, { useState } from "react";
import { isMobile, isTablet } from "react-device-detect";
import styled from "styled-components";
import { SpaceCreatorLinkType } from "@lifeat/shared/data-models/virtual-space/VirtualSpace";
import { MediaType, getSpaceMediaType } from "@lifeat/shared/spaces/SpacesUtil";
import { LifeAtButtonV2 } from "@common/LifeAtButtonV2/LifeAtButtonV2";
import { DropdownMenuComponents } from "@common/LifeAtDropdownMenuV2/DropdownMenuComponents";
import LifeAtVolumeSlider from "@common/LifeAtVolumeSlider/LifeAtVolumeSlider";
import LifeAtVolumeToggle from "@common/LifeAtVolumeToggle/LifeAtVolumeToggle";
import ProPillIndicator from "@common/PillIndicator/ProPillIndicator";
import { AppearanceContextType, useAppearanceContext } from "@contexts/AppearanceContext";
import { useToastListContext } from "@contexts/ToastListContext";
import { useUserContext } from "@contexts/UserContext";
import { useVirtualSpaceContext } from "@contexts/VirtualSpaceContext";
import MoreActionsIcons from "@icons/Menu-TripleVerticalDots-Icon.svg";
import ShareIcon from "@icons/Share-Icon.svg";
import { FontSize } from "@themes/Fonts";
import { BrowserUtil } from "@util/index";
import { isLegacyUserWithFreeSpaceAccess } from "../../../VirtualSpaceUtil";
import { getCreatorSocialLinkIconForKey } from "../SpaceCreatorInfo/SpaceCreatorInfo";
import SaveSpaceButton from "./SaveSpaceButton";

const SpaceNameAndCreatorContainer = styled.div`
    display: flex;
    flex: 1;
    align-self: stretch;
    flex-direction: column;
`;

const SpaceCreator = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 5px;
`;

const StyledMoreActionsIcons = styled(MoreActionsIcons)`
    fill: ${(props) => props.theme.iconColor};
`;

const ShareButton = styled(LifeAtButtonV2)`
    & .lifeat-button-content {
        padding: 4px;
    }
`;

const StyledShareIcon = styled(ShareIcon)`
    fill: ${(props) => props.theme.iconColor};
`;

const CreatorName = styled.span`
    font-size: ${FontSize.small};
    background-image: linear-gradient(45deg, #ff5353, #e74d67, #b87ce5, #a16ced, #ba6ebf);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    -moz-text-fill-color: transparent;
`;

const SpaceName = styled.span`
    font-size: ${FontSize.normal};
    color: ${(props) => props.theme.textColorPrimary};

    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
`;

const SpaceNameContainer = styled.div`
    display: flex;
    align-items: center;
    overflow: hidden;
    gap: 5px;
    max-width: 150px;
`;

const SpaceInfoContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    padding: 8px 16px;
    box-shadow: 0px -2px 10px -2px rgba(0, 0, 0, 0.2);
`;

const CurrentSpaceIndicator = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    height: 100%;
    width: 100%;
    padding: 8px 4px;
`;

const SPACE_VOLUME_LABEL = "Space Audio";

const REPORT_SPACE_LABEL = "Report space";
const REPORT_SPACE_LINK = "https://airtable.com/shrzBczpSUnFIiXWi";
const REPORT_CREATOR_LABEL = "Report creator";
const REPORT_CREATOR_LINK = "https://airtable.com/shrMWF1uTsaT1Qncj";

export default function CurrentVirtualSpace() {
    const { user } = useUserContext();
    const { lightMode }: AppearanceContextType = useAppearanceContext();
    const { spaceController } = useVirtualSpaceContext();
    const { addToastNotification } = useToastListContext();

    const [isCreatorInfoDropdownOpen, setIsCreatorInfoDropdownOpen] = useState(false);

    const spaceCreatorInfo = spaceController.selectedSpace.creatorName ? (
        <SpaceCreator>
            <CreatorName>@{spaceController.selectedSpace.creatorName}</CreatorName>

            <DropdownMenuComponents.Root
                open={isCreatorInfoDropdownOpen}
                onOpenChange={(isOpen) => setIsCreatorInfoDropdownOpen(isOpen)}
            >
                <DropdownMenuComponents.Trigger>
                    <LifeAtButtonV2 variant={"ghost"} size={"small"} shape={"square"}>
                        <StyledMoreActionsIcons />
                    </LifeAtButtonV2>
                </DropdownMenuComponents.Trigger>
                <DropdownMenuComponents.Content>
                    {Object.entries(spaceController.selectedSpace.creatorSocialLinks).map(
                        ([socialKey, socialUrl]: [SpaceCreatorLinkType, string]) => {
                            return (
                                socialKey &&
                                socialUrl && (
                                    <DropdownMenuComponents.Item
                                        key={socialKey}
                                        onClick={() => BrowserUtil.openLinkNewTab(socialUrl)}
                                    >
                                        {getCreatorSocialLinkIconForKey(socialKey, lightMode)}
                                        {socialKey}
                                    </DropdownMenuComponents.Item>
                                )
                            );
                        },
                    )}
                    <DropdownMenuComponents.Separator />
                    <DropdownMenuComponents.Item
                        key={`report-spaces-dropdown-item`}
                        onClick={() => BrowserUtil.openLinkNewTab(REPORT_SPACE_LINK)}
                    >
                        {REPORT_SPACE_LABEL}
                    </DropdownMenuComponents.Item>
                    <DropdownMenuComponents.Item
                        key={`report-creator-dropdown-iterm`}
                        onClick={() => BrowserUtil.openLinkNewTab(REPORT_CREATOR_LINK)}
                    >
                        {REPORT_CREATOR_LABEL}
                    </DropdownMenuComponents.Item>
                </DropdownMenuComponents.Content>
            </DropdownMenuComponents.Root>
        </SpaceCreator>
    ) : null;

    const showProPill =
        spaceController.selectedSpace.isPremium &&
        !isLegacyUserWithFreeSpaceAccess(user, spaceController.selectedSpace.category);

    const currSpaceIndicator = (
        <CurrentSpaceIndicator>
            <SpaceNameAndCreatorContainer>
                <SpaceNameContainer>
                    <SpaceName>{spaceController.selectedSpace.name}</SpaceName>
                    {showProPill && <ProPillIndicator className={`pro-space-indicator`} />}
                </SpaceNameContainer>

                {spaceCreatorInfo}
            </SpaceNameAndCreatorContainer>

            <ShareButton
                onClick={() => {
                    navigator.clipboard
                        .writeText(
                            new URL(`/app?space=${spaceController.selectedSpace.id}`, window.location.origin).href,
                        )
                        .then(() => addToastNotification("Success", "Space link copied to clipboard!"));
                }}
            >
                <StyledShareIcon />
            </ShareButton>

            <SaveSpaceButton
                currSpace={spaceController.selectedSpace}
                isCurrSpaceSaved={spaceController.isSpaceSaved(spaceController.selectedSpace)}
                toggleSpaceIsSaved={spaceController.toggleSpaceSaved}
            />
        </CurrentSpaceIndicator>
    );

    let spacesVolumeIndicator: React.ReactNode;
    if (isMobile || isTablet) {
        spacesVolumeIndicator = (
            <LifeAtVolumeToggle
                volume={spaceController.playerVolume}
                setVolume={spaceController.setPlayerVolume}
                label={SPACE_VOLUME_LABEL}
            />
        );
    } else {
        spacesVolumeIndicator = (
            <LifeAtVolumeSlider volume={spaceController.playerVolume} setVolume={spaceController.setPlayerVolume} />
        );
    }

    return (
        <SpaceInfoContainer>
            {currSpaceIndicator}
            {getSpaceMediaType(spaceController.selectedSpace) === MediaType.VIDEO && spacesVolumeIndicator}
        </SpaceInfoContainer>
    );
}
